import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, bulkAddSecondaryDomains } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import { PossibleDomain } from '../../../../../types';
import { Dispatch, SetStateAction, useState } from 'react';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useUserSettings } from '../../../../utils/use-user-settings';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface AddSecondaryDomainsDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: PossibleDomain[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
}

export default function AddSecondaryDomainsDialog({
  open,
  handleClose,
  selectedRows,
  setSelectedRows,
}: AddSecondaryDomainsDialogProps) {
  const { createEvent } = useAnalytics();
  const { changeLogRoles } = useUserSettings();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const selectedDomains = selectedRows.map((row) => row.domain);

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.UpdateSecondaryDomains.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      bulkAddSecondaryDomains(selectedDomains, changeLogMessage),
    onSuccess: (data) => {
      const failedDomains = Object.keys(data).filter((domain) => !data[domain]);

      if (failedDomains.length > 0) {
        setSnackbar({
          open: true,
          severity: 'error',
          message: `Unable to verify secondary domains for: ${failedDomains.join(
            ', '
          )}.`,
        });
      } else {
        setSnackbar({
          open: true,
          severity: 'success',
          message: `Secondary domains enabled successfully!`,
        });
      }

      queryClient.invalidateQueries({
        queryKey: [UserKeys.SecondaryDomains],
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.Domains],
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.UserSettings],
      });

      setSelectedRows([]);
      setChangeLogMessage('');

      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue enabling secondary domains. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.settings.enable_secondary_domain, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.settings,
      });
      setOpenChangeLogDialog(false);
    },
  });

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={
        selectedRows.length === 1
          ? 'Enable Secondary Domain'
          : 'Enable Secondary Domains'
      }
      dialogContent={
        <Box>
          <Typography>
            {selectedRows.length > 1 ? (
              <>
                Do you wish to enable <strong>{selectedRows.length}</strong>{' '}
                secondary domains?
              </>
            ) : selectedRows.length ? (
              <>
                Do you wish to enable the secondary domain{' '}
                <strong>{selectedRows[0].domain}</strong>?
              </>
            ) : (
              ''
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: selectedRows.length === 1 ? 'Enable Domain' : 'Enable Domains',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
